/**
 * The contract between the arena geometry and everything that queries it.
 *
 * Declared here, separately from any implementation, so that the systems which
 * consume collision queries (suspension raycasts, ball collision, sticky
 * forces, wall transitions) can be written and tested against a stub without
 * depending on the real arena mesh.
 *
 * All coordinates are world-space uu in the frame documented in math.ts.
 */

import { vec3, type Vec3 } from './math';
import type { OBB } from './obb';

/** A single surface contact. */
export interface Contact {
  /** Deepest point of overlap, on the surface. */
  point: Vec3;
  /** Unit surface normal, pointing OUT of the surface toward the query volume. */
  normal: Vec3;
  /**
   * Penetration depth along `normal`. Positive when overlapping; moving the
   * query volume by `normal * depth` resolves the overlap.
   */
  depth: number;
}

/** A reusable `Contact`, for the queries that fill a caller-owned buffer. */
export const makeContact = (): Contact => ({
  point: vec3(0, 0, 0),
  normal: vec3(0, 0, 0),
  depth: 0,
});

/** Result of a ray query. */
export interface RayHit {
  /** Distance along the ray direction from its origin. */
  distance: number;
  point: Vec3;
  /** Unit surface normal at the hit point. */
  normal: Vec3;
}

/**
 * Everything the simulation needs to know about the arena's shape.
 *
 * Implementations may be analytic (planes + fillets) or mesh-based (BVH over
 * the soccar triangle soup); nothing outside the arena module should care
 * which, so that the two can be swapped and compared.
 */
export interface ArenaGeometry {
  /**
   * Cast a ray and return the nearest hit within `maxDistance`, or null.
   * Used by the per-wheel suspension raycasts.
   *
   * `out`, when given, is a buffer the implementation MAY fill and return
   * instead of allocating — the suspension fires four of these per car per tick
   * and owns a result object per wheel already. It is a hint: an implementation
   * is free to ignore it, so a caller must compare the returned object with the
   * one it passed rather than assume.
   */
  raycast(origin: Vec3, direction: Vec3, maxDistance: number, out?: RayHit): RayHit | null;

  /**
   * Collide a sphere against the arena. Returns every contact found, which may
   * be more than one where surfaces meet (corner seams, the wall-ceiling
   * junction, the goal frame). Returns an empty array when there is no overlap.
   *
   * Multiple simultaneous contacts must be returned rather than merged: the
   * ball bounce model is applied per-contact, and merging them first changes
   * the result in exactly the corner cases this game is judged on.
   */
  collideSphere(center: Vec3, radius: number): Contact[];

  /**
   * Collide an oriented box against the arena — the car body.
   *
   * Fills `out` (a caller-owned buffer, so the hot path allocates nothing) with
   * at most `out.length` contacts and returns how many were written. Contacts
   * that share a normal direction are merged, keeping the deepest: a flat
   * surface is many coplanar triangles and each would otherwise contribute its
   * own full-depth push.
   *
   * OPTIONAL. The analytic stub arenas the car-controller tests run against have
   * no triangles to test a box against; a caller must treat its absence as "no
   * body contacts" rather than assuming it exists.
   */
  collideOBB?(box: OBB, out: Contact[]): number;

  /**
   * Nearest point on the arena surface to `point`, with its normal.
   * Used for sticky force and surface-transition decisions, where a full
   * sphere collision query would be wasteful.
   */
  nearestSurface(point: Vec3, maxDistance: number): RayHit | null;

  /** True when the position is inside the playable volume (ignoring goals). */
  contains(point: Vec3): boolean;
}

/**
 * Classify a surface normal into the category the car controller cares about.
 * The thresholds decide when a car is "on the wall" versus "on the floor",
 * which gates sticky force magnitude and the ground/air state machine.
 */
export const classifySurface = (normal: Vec3): 'floor' | 'wall' | 'ceiling' | 'ramp' => {
  if (normal.z > 0.7) return 'floor';
  if (normal.z < -0.7) return 'ceiling';
  if (Math.abs(normal.z) < 0.3) return 'wall';
  return 'ramp';
};
